/* eslint-disable react/no-unescaped-entities */
import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Modal } from 'antd';
import { ResponsiveContext } from 'App/lib/Hooks/ResponsiveContext';
import useLocalStorage from 'App/lib/Hooks/useLocalStorage';

// https://developer.mozilla.org/en-US/docs/Web/API/BeforeInstallPromptEvent
interface BeforeInstallPromptEvent {
  preventDefault: () => void;
  prompt: () => void;
}

export const usePwaLocalStorage = () =>
  useLocalStorage('pwaInstallPrompt.storage', {
    completed: false,
  });

// even though these are fixed constants, we need to make them dynamic
// so we can mock them in different tests
export const useMobileEnv = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigatorAny = window.navigator as any;
  const standalone =
    'standalone' in window.navigator && navigatorAny.standalone;
  return useMemo(
    () => ({
      navigator: navigatorAny,
      standalone,
      pwaInstalled:
        standalone || window.matchMedia('(display-mode: standalone)').matches,
      // since iOS 13 it's impossible to distinguish an iPad from MacOS using just the userAgent - use 'standalone'
      iOS:
        /iPhone|iPad/.test(window.navigator.userAgent) ||
        'standalone' in window.navigator,
      android: window.navigator.userAgent.includes('Android'),

      // cannot install PWAs from Chrome on iOS
      iOSChrome: window.navigator.userAgent.includes('CriOS'),
      chrome: window.navigator.userAgent.includes('Chrome'),
    }),
    []
  );
};

const PwaInstallPrompt = () => {
  const mobileEnv = useMobileEnv();
  const { mobileView } = useContext(ResponsiveContext);
  const [deferredInstall, setDeferredInstall] =
    useState<null | BeforeInstallPromptEvent>(null);
  const [storage, setStorage] = usePwaLocalStorage();
  const [visible, setVisible] = useState<boolean>(
    !mobileEnv.pwaInstalled && !storage.completed
  );
  const [showUsefulInfoCount, setShowUsefulInfoCount] = useState<number>(0);

  // the mobile "My Details" page includes an "Install to Device" option which reverts storage.completed to false
  useEffect(() => {
    if (!storage.completed && !mobileEnv.pwaInstalled) {
      setVisible(true);
      setShowUsefulInfoCount(0);
    }
  }, [storage.completed]);

  // catch the install prompt event on Chrome
  useEffect(() => {
    const listener = (e: Event) => {
      e.preventDefault();
      setDeferredInstall(e as unknown as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', listener);
    return () => window.removeEventListener('beforeinstallprompt', listener);
  }, []);

  const onInstallClick = async () => {
    if (deferredInstall) {
      deferredInstall.prompt();
      setDeferredInstall(null);
    }
    onCancelClick();
  };

  const onCancelClick = () => {
    setVisible(false);
    setStorage({ completed: true });
  };

  // only show the popup on small screens, or if we know we're on iOS or Android
  if (!(visible && (mobileView || mobileEnv.iOS || mobileEnv.android))) {
    return null;
  }

  let message: ReactNode;

  if (deferredInstall) {
    message = (
      <>
        <p data-testid="install-prompt">To install:</p>
        <ol style={{ paddingLeft: 18 }}>
          <li>Click the "Install" button below.</li>
          <li>Click "Install" or "OK" on the browser popup.</li>
        </ol>
      </>
    );
  } else if (mobileEnv.iOSChrome) {
    message = (
      <>
        <p data-testid="ioschrome-prompt">
          However, it cannot be installed using the Chrome browser. Please open
          this site in Safari and try again.
        </p>
      </>
    );
  } else if (mobileEnv.iOS && !mobileEnv.iOSChrome) {
    message = (
      <>
        <p data-testid="ios-prompt">To install:</p>
        <ol style={{ paddingLeft: 18 }}>
          <li>
            Tap the "Share" button:{' '}
            <img
              src="/images/ios-share-button.png"
              style={{ width: 24, height: 28, verticalAlign: 'bottom' }}
              alt="iOS Share Button"
            />
          </li>
          <li>
            Tap "Add to Home Screen:
            <br />
            <br />
            <img
              src="/images/ios-homescreen-menu.png"
              style={{ width: 326, maxWidth: '75vw' }}
              alt="iOS Add To HomeScreen"
            />
          </li>
        </ol>
      </>
    );
  } else if (mobileEnv.chrome && !mobileEnv.iOSChrome) {
    message = (
      <>
        <p data-testid="chrome-prompt">
          To install, click the "Add to Home screen" option in the chrome
          browser menu:
        </p>
        <p style={{ textAlign: 'center' }}>
          <img
            src="/images/chrome-add-homescreen.png"
            style={{ width: 244, height: 48 }}
            alt="Chrome Add to Homescreen"
          />
        </p>
      </>
    );
  } else {
    message = (
      <>
        <p data-testid="unknown-browser-prompt">
          However, it cannot be installed using this browser. Please open this
          site in Chrome and try again.
        </p>
      </>
    );
  }

  return (
    <Modal
      title={
        <span onClick={() => setShowUsefulInfoCount(showUsefulInfoCount + 1)}>
          Install To Device
        </span>
      }
      visible
      cancelText="Close"
      onCancel={onCancelClick}
      okText="Install"
      onOk={onInstallClick}
      okButtonProps={
        deferredInstall ? undefined : { style: { display: 'none' } }
      }
      style={{ top: 20 }}>
      <>
        <p>This website can be installed to your device just like an App.</p>
        {message}
        {showUsefulInfoCount > 5 && (
          <>
            pwaInstalled: {mobileEnv.pwaInstalled ? 'yes' : 'no'}
            <br />
            standalone:{' '}
            {mobileEnv.standalone
              ? 'yes'
              : typeof mobileEnv.navigator.standalone}
            <br />
            iOS: {mobileEnv.iOS ? 'yes' : 'no'}; Android:{' '}
            {mobileEnv.android ? 'yes' : 'no'}
            <br />
            user agent: {window.navigator.userAgent}
          </>
        )}
      </>
    </Modal>
  );
};

export default PwaInstallPrompt;
